import React, { useEffect, useState, useCallback } from "react";
import { useRouterStore } from "../../../stores";
import { Badge } from "antd";
import {
  CloseCircleOutlined,
  InfoCircleOutlined,
  KeyOutlined,
  CameraOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { DialogCloseSession } from "../../../../root/components/dialog";
import { ChangePasswordModal, ChangeAvatarModal } from "./modal-user-quick";
import "../../css/user-avatar.css";

// ── Avatar con fallback a iniciales si la imagen falla ───────────────────────
const AvatarDisplay = ({
  src,
  name,
  className,
  style,
}: {
  src?: string | null;
  name?: string | null;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const [imgError, setImgError] = React.useState(false);
  const showImg = src && !imgError;
  return showImg ? (
    <img
      src={src}
      alt={name ?? ""}
      className={className}
      style={style}
      onError={() => setImgError(true)}
    />
  ) : (
    <div
      className={className}
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: "0.02em",
      }}
    >
      {getInitials(name)}
    </div>
  );
};
const getInitials = (name?: string | null): string => {
  if (!name) return "U";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

export const UserAvatar = () => {
  const navigate = useNavigate();
  const firstName = useRouterStore((s) => s.firstName);
  const avatar = useRouterStore((s) => s.avatar);

  const [openSession, setOpenSession] = useState(false);
  const [openPass, setOpenPass] = useState(false);
  const [openAvatar, setOpenAvatar] = useState(false);
  const [pendingItems, setPendingItems] = useState(0);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [queueModalVisible, setQueueModalVisible] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [swVersion, setSwVersion] = useState("v1.0.0");
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    setSwVersion(process.env.REACT_APP_VERSION ?? "v1.0.0");
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const closeDropdown = () => setDropdownVisible(false);

  // ── FIX: abrir modal de sesión sin re-render pesado ─────────────────────────
  const handleOpenSession = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closeDropdown();
    // requestAnimationFrame garantiza que el dropdown cierre antes de montar el modal
    requestAnimationFrame(() => setOpenSession(true));
  }, []);

  const handleCloseSession = useCallback(() => setOpenSession(false), []);

  const handleOpenPass = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closeDropdown();
    requestAnimationFrame(() => setOpenPass(true));
  }, []);

  const handleOpenAvatar = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    closeDropdown();
    requestAnimationFrame(() => setOpenAvatar(true));
  }, []);
  const handleQueueModalClose = useCallback(() => {
    setQueueModalVisible(false);
  }, []);

  // ── Menú personalizado ──────────────────────────────────────────────────────
  const DropdownMenu = (
    <div className="ua-menu">
      {/* Perfil */}
      <div
        className="ua-profile"
        onClick={() => {
          navigate("/user/information");
          closeDropdown();
        }}
      >
        <div className="ua-profile-avatar">
          <AvatarDisplay
            src={avatar}
            name={firstName}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              objectFit: "cover" as const,
            }}
          />
        </div>
        <div className="ua-profile-info">
          <div className="ua-profile-name">{firstName ?? "Usuario"}</div>
          <div className="ua-profile-sub">Ver perfil →</div>
        </div>
      </div>

      <div className="ua-divider" />

      {/* Cambiar avatar */}
      <div className="ua-item avatar" onClick={handleOpenAvatar}>
        <div className="ua-item-icon">
          <CameraOutlined />
        </div>
        <span className="ua-item-label">Cambiar avatar</span>
      </div>

      {/* Cambiar contraseña */}
      <div className="ua-item key" onClick={handleOpenPass}>
        <div className="ua-item-icon">
          <KeyOutlined />
        </div>
        <span className="ua-item-label">Cambiar contraseña</span>
      </div>

      <div className="ua-divider" />

      <div className="ua-divider" />

      {/* Cerrar sesión */}
      <div className="ua-item logout" onClick={handleOpenSession}>
        <div className="ua-item-icon">
          <CloseCircleOutlined />
        </div>
        <span className="ua-item-label">Cerrar sesión</span>
      </div>

      <div className="ua-divider" />

      {/* Versión */}
      <div className="ua-item disabled">
        <div className="ua-item-icon" style={{ color: "#999" }}>
          <InfoCircleOutlined />
        </div>
        <span className="ua-version-text">Versión {swVersion}</span>
        <div className={`ua-status-dot ${isOnline ? "online" : "offline"}`} />
      </div>
    </div>
  );

  return (
    <div>
      {/* Modal cerrar sesión */}
      <DialogCloseSession open={openSession} handleClose={handleCloseSession} />

      {/* Modales de cuenta */}
      <ChangePasswordModal open={openPass} onClose={() => setOpenPass(false)} />
      <ChangeAvatarModal
        open={openAvatar}
        onClose={() => setOpenAvatar(false)}
      />

      {/* Dropdown con menú personalizado */}
      <div style={{ position: "relative" }}>
        <Badge
          dot={pendingItems > 0 || updateAvailable}
          offset={[-5, 5]}
          color={
            updateAvailable
              ? "#fa8c16"
              : pendingItems > 0
                ? "#ef4444"
                : undefined
          }
        >
          <div
            onClick={() => setDropdownVisible((v) => !v)}
            style={{ cursor: "pointer" }}
          >
            <AvatarDisplay
              src={avatar}
              name={firstName}
              className="avatar-element"
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                objectFit: "cover" as const,
              }}
            />
          </div>
        </Badge>

        {/* Menú custom */}
        {dropdownVisible && (
          <>
            {/* Overlay para cerrar */}
            <div
              style={{ position: "fixed", inset: 0, zIndex: 999 }}
              onClick={closeDropdown}
            />
            <div
              style={{
                position: "fixed",
                top: "57px",
                right: "8px",
                zIndex: 1000,
              }}
            >
              {DropdownMenu}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
